import React from 'react';
import {
  Box,
  Button as MuiButton,
  Chip,
  Paper,
  Stack,
  Typography,
  alpha,
} from '@mui/material';
import {
  AlertCircle,
  CheckCircle2,
  Lock,
} from 'lucide-react';
import { jobOpeningDetailsTabs } from '../../../../../../utils/types/JobOpenings';
import { IStatusMessageProps } from '../interfaces';

export const StatusMessageContent = ({
  status,
  requiredTabs,
  enabledElement,
  setCurrentPage,
  setStatusTab,
}: IStatusMessageProps) => {
  const handleTabClick = (tab: string) => {
    if (setCurrentPage && setStatusTab) {
      setStatusTab(tab as jobOpeningDetailsTabs);
      setCurrentPage('Job Opening Details');
    }
  };

  if (status === 'loading') {
    return null;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        minHeight: 380,
        p: 3,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          maxWidth: 520,
          width: '100%',
          textAlign: 'center',
          p: { xs: 3, sm: 4 },
          borderRadius: 3,
          border: '1px solid #e2e8f0',
          bgcolor: '#ffffff',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.03)',
        }}
      >
        {status === 'disabled' && (
          <Stack spacing={2.5} alignItems='center'>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                bgcolor: '#f1f5f9',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#64748b',
              }}
            >
              <Lock size={28} />
            </Box>

            <Box>
              <Typography
                variant='h6'
                sx={{ fontWeight: 700, color: '#1e293b', mb: 0.5 }}
              >
                Feature Disabled
              </Typography>
              <Typography variant='body2' sx={{ color: '#64748b' }}>
                This section is currently disabled for this job opening.
              </Typography>
            </Box>

            {enabledElement && (
              <MuiButton
                onClick={enabledElement}
                variant='contained'
                color='primary'
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 3,
                  py: 1,
                  borderRadius: 2,
                }}
              >
                Enable Section
              </MuiButton>
            )}
          </Stack>
        )}

        {status === 'requiredCompletion' && requiredTabs && (
          <Stack spacing={2.5} alignItems='center'>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'primary.main',
              }}
            >
              <AlertCircle size={28} />
            </Box>

            <Box>
              <Typography
                variant='h6'
                sx={{ fontWeight: 700, color: '#1e293b', mb: 0.5 }}
              >
                Action Required
              </Typography>
              <Typography variant='body2' sx={{ color: '#64748b' }}>
                Please configure and complete the following tabs before proceeding:
              </Typography>
            </Box>

            <Stack
              direction='row'
              flexWrap='wrap'
              gap={1}
              justifyContent='center'
              sx={{ width: '100%' }}
            >
              {requiredTabs.map((tab, index) => (
                <Chip
                  key={index}
                  icon={<CheckCircle2 size={14} />}
                  label={tab}
                  onClick={() => handleTabClick(tab)}
                  clickable
                  sx={{
                    bgcolor: '#f8fafc',
                    border: '1px solid #cbd5e1',
                    color: '#334155',
                    fontWeight: 600,
                    fontSize: 13,
                    py: 2,
                    px: 0.5,
                    transition: 'all 0.2s',
                    '&:hover': {
                      bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                      borderColor: 'primary.main',
                      color: 'primary.main',
                    },
                    '& .MuiChip-icon': {
                      color: 'primary.main',
                    },
                  }}
                />
              ))}
            </Stack>
          </Stack>
        )}
      </Paper>
    </Box>
  );
};
